import { useEffect, useId, useMemo, useState } from 'react';
import { IStopArea } from '../../../../../models/stop-area.model';
import { t } from '../../../../../services/texts.service';
import { ApiPlaneraResaService } from '../../../../../services/api-planera-resa/api-planera-resa.service';
import { JourneyHelper } from '../../../../../helpers/journey.helper';
import { VasttrafikGidHelper } from '../../../../../helpers/vasttrafik-gid.helper';
import { IJourney } from '../../../../../models/journey.model';
import LineBadge from '../../../../Shared/LineBadge';
import { DateTimeHelper } from '../../../../../helpers/date-time.helper';
import LogService from '../../../../../services/log-service/log.service';
import Alert from '../../../../Shared/Alert';
import Spinner from '../../../../Shared/Spinner';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { DateTime } from 'luxon';
import useFetch, { DataSource, Status } from '../../../../../hooks/fetch.hook';
import { JourneyDetailsApiModel } from '../../../../../services/api-planera-resa/models/journey-details/journey-details.api-model';
import Details from './components/Details';
import { IJourneyDetails } from '../../../../../models/journey-details.model';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { TransportSubMode } from '../../../../../models/transport-sub-mode.enum';
import type { IErrorResponse } from '../../../../../models/error-response.model';
import { ErrorHandlerHelper } from '../../../../../helpers/error-handler.helper';
import { ErrorResponseHelper } from '../../../../../helpers/error-response.helper';
import { TranslationService } from '../../../../../services/translation.service';

function removeSlowerJourneysForSameDeparture(journeys: IJourney[]): IJourney[] {
  const departureMap: { [departureTime: string]: IJourney } = {};
  journeys.forEach((journey) => {
    const gid = JourneyHelper.getFirstPublicTransportLeg(journey)?.serviceJourney.gid;
    if (!gid) return;
    if (
      !departureMap[gid] ||
      JourneyHelper.getTripLegIsCancelled(departureMap[gid]) ||
      JourneyHelper.getDurationInMinutes(journey, true) < JourneyHelper.getDurationInMinutes(departureMap[gid], true)
    ) {
      departureMap[gid] = journey;
    }
  });
  return Object.values(departureMap);
}

function sortJourneysByDepartureTime(journeys: IJourney[]): IJourney[] {
  return [...journeys].sort((journeyA, journeyB) => {
    const departureTimeA = JourneyHelper.getDepartureTimeForJourney(journeyA, true)?.toMillis() ?? Number.MAX_SAFE_INTEGER;
    const departureTimeB = JourneyHelper.getDepartureTimeForJourney(journeyB, true)?.toMillis() ?? Number.MAX_SAFE_INTEGER;

    return departureTimeA - departureTimeB;
  });
}

function getLineGidsForJourneys(journeys: IJourney[]): string[] {
  const lineGids = journeys
    .flatMap((journey) => journey.tripLegs ?? [])
    .map((leg) => VasttrafikGidHelper.getLineGidFromServiceJourneyGid(leg.serviceJourney.gid))
    .filter((gid): gid is string => gid !== undefined);
  return [...new Set(lineGids)];
}

function updateJourneyMetadata(
  journeys: IJourney[] | undefined,
  onJourneysLineGidsChange?: (lineGids: string[] | undefined) => void,
  onJourneysTransportModesChange?: (transportModes: TransportMode[] | undefined) => void,
) {
  onJourneysLineGidsChange?.(journeys ? getLineGidsForJourneys(journeys) : undefined);
  onJourneysTransportModesChange?.(journeys ? JourneyHelper.getTransportModesForJourneys(journeys) : undefined);
}

export default function Trips({
  from,
  to,
  showOnlyFastestTripPerDeparture,
  autoRefreshTimestamp,
  onJourneysLineGidsChange,
  onJourneysTransportModesChange,
  showLeavesIn,
  showDepartureTime,
  showOnlyFirstBadgeInSummary,
  showOriginStopName,
  transportModes,
  transportSubModes,
  className,
}: {
  from: IStopArea | IStopArea[] | undefined;
  to: IStopArea | IStopArea[] | undefined;
  showOnlyFastestTripPerDeparture: boolean;
  autoRefreshTimestamp: DateTime;
  onJourneysLineGidsChange?: (lineGids: string[] | undefined) => void;
  onJourneysTransportModesChange?: (transportModes: TransportMode[] | undefined) => void;
  showLeavesIn: boolean;
  showDepartureTime: boolean;
  showOnlyFirstBadgeInSummary: boolean;
  showOriginStopName: boolean;
  transportModes: TransportMode[];
  transportSubModes: TransportSubMode[];
  className?: string;
}) {
  // === STATE ===
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [journeys, setJourneys] = useState<IJourney[]>([]);
  const [errorHasOccurred, setErrorHasOccurred] = useState<boolean>(false);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);

  // === VARIABLES ===
  const originStopAreas = useMemo(() => (Array.isArray(from) ? from : from ? [from] : []), [from]);
  const destinationStopAreas = useMemo(() => (Array.isArray(to) ? to : to ? [to] : []), [to]);
  const hasOriginStopAreas = originStopAreas.length > 0;
  const hasDestinationStopAreas = destinationStopAreas.length > 0;
  const canFetchTrips = hasOriginStopAreas && hasDestinationStopAreas;
  const selectStopAreasMessage =
    !hasOriginStopAreas && !hasDestinationStopAreas
      ? t('Välj hållplatser för att se dina resor.', 'Select stop areas to see your trips.')
      : !hasOriginStopAreas
        ? t('Välj från-hållplats för att se dina resor.', 'Select a from stop area to see your trips.')
        : t('Välj till-hållplats för att se dina resor.', 'Select a destination stop area to see your trips.');

  // === EFFECTS ===
  useEffect(() => {
    if (!canFetchTrips) {
      updateJourneyMetadata(undefined, onJourneysLineGidsChange, onJourneysTransportModesChange);
      return;
    }

    async function fetchTrips() {
      try {
        setErrorHasOccurred(false);
        setErrorResponse(undefined);
        setIsLoading(true);
        const results = await Promise.allSettled(
          originStopAreas.flatMap((origin) =>
            destinationStopAreas.map((destination) =>
              ApiPlaneraResaService.searchJourneys({
                originGid: origin.gid,
                destinationGid: destination.gid,
                transportModes,
                transportSubModes,
                nearbyStopAreas: false,
              }),
            ),
          ),
        );

        const successfulResults = results
          .filter((result): result is PromiseFulfilledResult<Awaited<ReturnType<typeof ApiPlaneraResaService.searchJourneys>>> => result.status === 'fulfilled')
          .map((result) => result.value);

        results.forEach((result) => {
          if (result.status === 'rejected') {
            LogService.logError(result.reason, 'Error occurred while searching for one journey combination in My Stop Areas');
          }
        });

        if (successfulResults.length === 0) {
          setJourneys([]);
          updateJourneyMetadata(undefined, onJourneysLineGidsChange, onJourneysTransportModesChange);
          setErrorHasOccurred(true);
          const firstRejected = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
          if (firstRejected && ErrorHandlerHelper.isErrorResponse(firstRejected.reason)) {
            setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(firstRejected.reason as IErrorResponse, (x) => TranslationService.get(x)));
          }
          setIsLoading(false);
          return;
        }

        const journeysWithIds = JourneyHelper.setJourneyIds(
          successfulResults.flatMap((result) => result.results),
          0,
          false,
        );
        const sortedJourneys = sortJourneysByDepartureTime(journeysWithIds);
        setJourneys(sortedJourneys);
        updateJourneyMetadata(sortedJourneys, onJourneysLineGidsChange, onJourneysTransportModesChange);
        setIsLoading(false);
      } catch (error) {
        if (ErrorHandlerHelper.isErrorResponse(error)) {
          setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
        }
        LogService.logError(error, 'Error occurred while searching for journeys in My Stop Areas');
        setJourneys([]);
        updateJourneyMetadata(undefined, onJourneysLineGidsChange, onJourneysTransportModesChange);
        setErrorHasOccurred(true);
        setIsLoading(false);
      }
    }

    fetchTrips();
  }, [autoRefreshTimestamp, canFetchTrips, destinationStopAreas, originStopAreas, transportModes, transportSubModes, onJourneysLineGidsChange, onJourneysTransportModesChange]);

  const filteredJourneys = showOnlyFastestTripPerDeparture ? removeSlowerJourneysForSameDeparture(journeys) : journeys;
  const visibleJourneys = filteredJourneys.slice(0, 10);
  const timesInformationText = showLeavesIn
    ? showDepartureTime
      ? t('Tiderna anger avgångstid, om hur många minuter resan avgår respektive restid.', 'Times indicate departure time, in how many minutes the trip leaves and travel time.')
      : t('Tiderna anger om hur många minuter resan avgår respektive restid.', 'Times indicate in how many minutes the trip leaves and travel time.')
    : showDepartureTime
      ? t('Tiderna anger avgångstid respektive restid.', 'Times indicate departure time and travel time.')
      : t('Tiderna anger restiden.', 'Times indicate travel time.');

  // === RENDERING ===
  return (
    <div className={className ?? ''}>
      <h3>{t('Kommande resor', 'Upcoming trips')}</h3>
      {isLoading && <Spinner className="my-3" />}
      {!canFetchTrips && <span>{selectStopAreasMessage}</span>}
      <Alert
        heading={t('Ett fel uppstod vid hämtning av resor. Försök igen senare.', 'An error occurred while fetching trips. Please try again later.')}
        closable={false}
        show={errorHasOccurred && errorResponse?.errorCode !== 2020}
      />
      <Alert
        heading={errorResponse?.errorMessage || t('Det finns inga resor på sträckan.', 'There are no trips for the search.')}
        closable={false}
        show={errorHasOccurred && errorResponse !== undefined && errorResponse?.errorCode === 2020}
      />
      {canFetchTrips && !isLoading && !errorHasOccurred && (
        <>
          <p>{timesInformationText}</p>
          <div>
            {filteredJourneys.length === 0 && <div className="text-muted">{t('Inga kommande resor hittades.', 'No upcoming trips found.')}</div>}
            {visibleJourneys.length > 0 && (
              <ul className="list-unstyled d-flex flex-column gap-2">
                {visibleJourneys.map((journey) => (
                  <li key={journey.id}>
                    <TripSummary
                      journey={journey}
                      showLeavesIn={showLeavesIn}
                      showDepartureTime={showDepartureTime}
                      showOriginStopName={showOriginStopName && originStopAreas.length > 1}
                      showOnlyFirstBadgeInSummary={showOnlyFirstBadgeInSummary}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TripSummary({
  journey,
  showLeavesIn,
  showDepartureTime,
  showOriginStopName,
  showOnlyFirstBadgeInSummary,
}: {
  journey: IJourney;
  showLeavesIn: boolean;
  showDepartureTime: boolean;
  showOriginStopName: boolean;
  showOnlyFirstBadgeInSummary: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const detailsId = useId();
  const isTripLegCancelled = JourneyHelper.getTripLegIsCancelled(journey);
  const isTripLegPartiallyCancelled = JourneyHelper.getTripLegIsPartCancelled(journey);
  const backgroundColorClass = isTripLegCancelled || isTripLegPartiallyCancelled ? 'bg-gray' : 'bg-blue';
  const firstStopPointName = JourneyHelper.getFirstPublicTransportLeg(journey)?.origin.stopPoint.name;

  return (
    <div>
      <button
        type="button"
        className={`${backgroundColorClass} ${isExpanded ? ' shadow rounded-top rounded-bottom-0' : 'rounded'} border p-2 p-sm-3 d-flex justify-content-between align-items-center gap-2 w-100 text-start btn overflow-auto fw-normal`}
        onClick={() => {
          setIsExpanded(!isExpanded);
          AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Reseförslag', isExpanded ? 'Minimera detaljer' : 'Expandera detaljer');
        }}
        aria-expanded={isExpanded}
        aria-controls={detailsId}
      >
        <div className="d-flex justify-content-between flex-nowrap flex-grow-1 gap-2">
          <div className="d-flex flex-column gap-1">
            {showOriginStopName && firstStopPointName && <span className="fw-bold">{firstStopPointName}</span>}
            <LineBadges journey={journey} isExpanded={isExpanded} showOnlyFirstBadgeInSummary={showOnlyFirstBadgeInSummary} />
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end">
            {showDepartureTime && <DepartureTime journey={journey} />}
            {showLeavesIn && <DepartureIn journey={journey} />}
            {!isTripLegCancelled && !isTripLegPartiallyCancelled && <TravelTime journey={journey} />}
            {isTripLegCancelled && <span className="text-danger fw-bold">{t('Inställd', 'Cancelled')}</span>}
            {!isTripLegCancelled && isTripLegPartiallyCancelled && <span className="text-danger fw-bold">{t('Delvis inställd', 'Partially cancelled')}</span>}
          </div>
        </div>
      </button>
      {isExpanded && <TripDetails id={detailsId} className="shadow" journey={journey} />}
    </div>
  );
}

function TripDetails({ id, className, journey }: { id: string; className?: string; journey: IJourney }) {
  function processReceivedData(data: JourneyDetailsApiModel | undefined, journey: IJourney): IJourneyDetails | undefined {
    if (!data) return undefined;

    try {
      const mappedData = JourneyDetailsApiModel.mapToBusinessModel(data);

      return {
        ...mappedData,
        departureAccessLink: mappedData.departureAccessLink,
        arrivalAccessLink: journey.tripLegs && journey.tripLegs.length === 0 ? journey.arrivalAccessLink : mappedData.arrivalAccessLink,
        connectionLinks: mappedData.connectionLinks,
        tripLegs: mappedData.tripLegs,
      };
    } catch (error) {
      LogService.logError(error, 'Error mapping JourneyDetailsApiModel to business model in JourneyDetails');
      return undefined;
    }
  }

  const url = `/journeys/${journey.detailsReference}/details?includes=links&includes=ServiceJourneyCalls`;

  const [getJourneyDetails] = useFetch<JourneyDetailsApiModel | undefined>(url, false, false, {
    dataSource: DataSource.apiPlaneraResa,
    keepLastStateIfPrevented: false,
    delayInMs: 0,
  });

  const journeyDetails = processReceivedData(getJourneyDetails.data, journey);

  return (
    <div id={id} className={`border border-top-0 rounded-bottom p-2 p-sm-3 mt-0 bg-white ${className}`}>
      <div className="small">
        {getJourneyDetails.status === Status.loading && <Spinner className="my-2" />}
        <Alert
          heading={t('Ett fel uppstod vid hämtning av resans detaljer. Försök igen senare.', 'An error occurred while fetching trip details. Please try again later.')}
          closable={false}
          show={getJourneyDetails.status === Status.failed || (getJourneyDetails.status === Status.success && !journeyDetails)}
        />
        {getJourneyDetails.status === Status.success && journeyDetails && <Details journey={journey} journeyDetails={journeyDetails} />}
      </div>
    </div>
  );
}

function LineBadges({ journey, isExpanded, showOnlyFirstBadgeInSummary }: { journey: IJourney; isExpanded: boolean; showOnlyFirstBadgeInSummary?: boolean }) {
  const tripLegsWithLine = journey.tripLegs?.filter((tripLeg) => tripLeg.serviceJourney.line) ?? [];
  const visibleTripLegs = isExpanded || !showOnlyFirstBadgeInSummary ? tripLegsWithLine : tripLegsWithLine.slice(0, 1);
  const hiddenBadgeCount = Math.max(tripLegsWithLine.length - visibleTripLegs.length, 0);

  return (
    <div className={`d-flex ${isExpanded && showOnlyFirstBadgeInSummary ? 'flex-wrap' : 'flex-nowrap'} align-items-center gap-1`}>
      {visibleTripLegs.map((tripLeg) => (
        <LineBadge key={tripLeg.serviceJourney.gid} line={tripLeg.serviceJourney.line!} useShortName={true} className="" />
      ))}
      {!isExpanded && hiddenBadgeCount > 0 && (
        <span className="badge d-flex align-items-center align-self-stretch text-color-gray-900 border top-0 px-1">
          <span className="visually-hidden">{t('Ytterligare linjer', 'Additional lines')}</span>
          <span className="fw-bold">+{hiddenBadgeCount}</span>
        </span>
      )}
    </div>
  );
}

function DepartureTime({ journey }: { journey: IJourney }) {
  const isDepartureOnTime = JourneyHelper.isDepartureOnTime(journey);

  if (!isDepartureOnTime) {
    return (
      <span className="text-nowrap">
        <span className="visually-hidden">
          {t('Ursprunglig', 'Original')} {t.departureTime}
        </span>
        <span className={`text-decoration-line-through text-muted me-1`}>{DateTimeHelper.toTime(JourneyHelper.getDepartureTimeForJourney(journey, false))}</span>
        <span className="visually-hidden">
          {t('Beräknad', 'Estimated')} {t.departureTime}
        </span>
        <span className="fw-bold bg-yellow px-1">{DateTimeHelper.toTime(JourneyHelper.getDepartureTimeForJourney(journey, true))}</span>
      </span>
    );
  }

  return (
    <span className="text-nowrap">
      <span className="visually-hidden">{t.departureTime}</span>
      <span className="fw-bold">{DateTimeHelper.toTime(JourneyHelper.getDepartureTimeForJourney(journey, true))}</span>
    </span>
  );
}

function TravelTime({ journey }: { journey: IJourney }) {
  const departureTime = JourneyHelper.getDepartureTimeForJourney(journey, true);
  const arrivalTime = JourneyHelper.getArrivalTimeForJourney(journey, true);

  if (!departureTime || !arrivalTime) {
    return null;
  }

  return (
    <span className="text-nowrap">
      <span className="visually-hidden">{t('Restid', 'Travel time')}</span>
      {DateTimeHelper.toDuration(departureTime, arrivalTime)}
    </span>
  );
}

function DepartureIn({ journey }: { journey: IJourney }) {
  const isDepartureOnTime = JourneyHelper.isDepartureOnTime(journey);
  const departureTime = JourneyHelper.getDepartureTimeForJourney(journey, !isDepartureOnTime);

  if (!departureTime) return null;

  const minutesUntilDeparture = DateTimeHelper.minutes(DateTime.now(), departureTime);

  if (minutesUntilDeparture <= 0) {
    return (
      <>
        <span className="visually-hidden">{t('Avgår nu', 'Leaves now')}</span>
        <span aria-hidden="true">{t('Nu', 'Now')}</span>
      </>
    );
  }

  return (
    <div className="text-nowrap">
      <span className="visually-hidden">{t('Avgår om', 'Leaves in')}</span>
      <span aria-hidden="true">{t('Om', 'In')} </span>
      <span className="fw-bold">
        {minutesUntilDeparture} {t('min', 'min')}
      </span>
    </div>
  );
}